/**
 * PlatformAdminShell — the ONE shell for platform super-admin surfaces
 * (docs/PLATFORM-ADMIN-ARCHITECTURE.md §1). House admin style (the same as the Sports
 * super admin): dark canvas, Oswald headings, Manrope body, red accent.
 *
 *   header   — back to /superadmin · kicker "Super Admin — {product}" · title · headerRight
 *   products — POS · Sports · OrbitPrint (the sub-areas; the active one is lit)
 *   shelves  — the product's shelves that EXIST today (Organizations · Log …); the active one is lit
 *   crumbs   — optional breadcrumbs for nested entities (Organizations / Hardware Inc)
 *
 * Only the retail super admin reaches these pages (the endpoint gate is the enforcement;
 * `useSuperAdminGate` just keeps a non-admin from staring at a broken page).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';

export const HOUSE = {
  canvas: '#0A0A0A', header: '#0D0D0D', card: '#141414', line: '#1E1E1E', line2: '#2A2A2A',
  accent: '#E63946', text: '#fff', body: '#ccc', mute: '#737373', dim: '#555', faint: '#333',
  ok: '#00E676', warn: '#FF9800', amber: '#FFB300', bad: '#FF2D55', info: '#00BCD4', gold: '#FFD700',
};

export const PRODUCTS = [
  { k: 'pos', label: 'POS', to: '/superadmin' },
  { k: 'sports', label: 'Sports', to: '/sports/super-admin' },
  { k: 'orbitprint', label: 'OrbitPrint', to: '/superadmin/orbitprint/orgs' },
];

export const card = { background: HOUSE.card, border: `1px solid ${HOUSE.line}`, borderRadius: 12 };
export const sectionTitle = { color: HOUSE.dim, fontWeight: 800, letterSpacing: 1 };

/** Pill button used for filters / attention chips. */
export function Chip({ active, color = HOUSE.accent, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      style={{ padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
        background: active ? color : `${color}14`, color: active ? '#000' : color, border: `1px solid ${color}40`, transition: 'all .15s' }}>
      {children}
    </button>
  );
}

export function Badge({ color, bg, children, testId }) {
  return (
    <span data-testid={testId} style={{ fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 5, background: bg || `${color}14`, color,
      textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

/** House-styled modal for one-step actions on an entity (architecture §5). */
export function Modal({ title, onClose, children, width = 480, testId }) {
  return (
    <div onClick={onClose} data-testid={testId}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} className="w-full" style={{ ...card, border: `1px solid ${HOUSE.line2}`, maxWidth: width, maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between" style={{ padding: '14px 18px', borderBottom: `1px solid ${HOUSE.line}` }}>
          <p className="font-bold uppercase text-sm" style={{ fontFamily: 'Oswald, sans-serif', color: HOUSE.text }}>{title}</p>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: HOUSE.dim, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 18 }}>{children}</div>
      </div>
    </div>
  );
}

export const inputStyle = { background: HOUSE.canvas, border: `1px solid ${HOUSE.line2}`, color: HOUSE.text, borderRadius: 8, padding: '8px 12px', width: '100%', outline: 'none', fontSize: 13, boxSizing: 'border-box' };

export function ActionButton({ onClick, children, tone = 'quiet', disabled, testId, small }) {
  const tones = {
    quiet: { background: HOUSE.card, border: `1px solid ${HOUSE.line2}`, color: HOUSE.body },
    primary: { background: HOUSE.accent, border: `1px solid ${HOUSE.accent}`, color: '#fff' },
    danger: { background: `${HOUSE.bad}14`, border: `1px solid ${HOUSE.bad}55`, color: HOUSE.bad },
    ok: { background: `${HOUSE.ok}14`, border: `1px solid ${HOUSE.ok}55`, color: HOUSE.ok },
    amber: { background: `${HOUSE.amber}14`, border: `1px solid ${HOUSE.amber}55`, color: HOUSE.amber },
  };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      style={{ ...tones[tone], borderRadius: 8, padding: small ? '5px 10px' : '8px 14px', fontSize: small ? 11 : 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap' }}>
      {children}
    </button>
  );
}

/** Route states are part of the architecture (universal §18). */
export function RouteState({ kind, message, onRetry }) {
  if (kind === 'loading') {
    return (
      <div className="flex items-center justify-center py-20" data-testid="platform-admin-loading">
        <div className="animate-spin w-8 h-8 rounded-full" style={{ border: `2px solid ${HOUSE.accent}`, borderTopColor: 'transparent' }} />
      </div>
    );
  }
  const icon = { empty: '🛰', error: '⚠', denied: '🔒', notfound: '∅' }[kind] || '·';
  return (
    <div className="text-center py-16" data-testid={`platform-admin-${kind}`} style={{ color: HOUSE.faint }}>
      <p className="text-4xl mb-3">{icon}</p>
      <p className="text-sm" style={{ color: HOUSE.mute }}>{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="mt-4 px-4 py-2 rounded-lg text-xs font-bold uppercase"
          style={{ background: HOUSE.card, border: `1px solid ${HOUSE.line2}`, color: HOUSE.body }}>Try again</button>
      )}
    </div>
  );
}

/** Keeps a non-admin off these pages; the endpoint gate is the real enforcement. */
export function useSuperAdminGate() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('agripos_token');
    if (!token) { navigate('/login', { replace: true }); return; }
    setReady(true);
  }, [navigate]);
  return ready;
}

export default function PlatformAdminShell({ product = 'orbitprint', title, kicker, shelves = [], active, crumbs = [], headerRight = null, children }) {
  const navigate = useNavigate();
  const productLabel = PRODUCTS.find(p => p.k === product)?.label || product;
  return (
    <div style={{ minHeight: '100vh', background: HOUSE.canvas, color: HOUSE.text, fontFamily: 'Manrope, sans-serif' }} data-testid={`platform-admin-${product}`}>
      <div style={{ background: HOUSE.header, padding: '18px 16px 0 16px', borderBottom: `1px solid ${HOUSE.line}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
            <button onClick={() => navigate('/superadmin')} data-testid="platform-admin-back"
              style={{ background: 'transparent', border: 'none', color: HOUSE.dim, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 4, flexShrink: 0 }}>
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: HOUSE.accent, fontWeight: 700, marginBottom: 2 }}>
                {kicker || `Super Admin — ${productLabel}`}
              </p>
              <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 'clamp(17px, 5vw, 24px)', fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1, overflowWrap: 'anywhere' }}>
                {title}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1" data-testid="platform-admin-products" style={{ background: HOUSE.card, border: `1px solid ${HOUSE.line2}`, borderRadius: 8, padding: 3 }}>
              {PRODUCTS.map(p => (
                <button key={p.k} onClick={() => p.k !== product && navigate(p.to)} data-testid={`platform-admin-product-${p.k}`}
                  style={{ padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, cursor: p.k === product ? 'default' : 'pointer',
                    background: p.k === product ? HOUSE.accent : 'transparent', color: p.k === product ? '#fff' : HOUSE.dim, border: 'none' }}>
                  {p.label}
                </button>
              ))}
            </div>
            {headerRight}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, marginTop: 14, overflowX: 'auto' }} data-testid="platform-admin-shelves">
          {shelves.map(s => (
            <button key={s.k} onClick={() => navigate(s.to)} data-testid={`platform-admin-shelf-${s.k}`}
              style={{ padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1,
                color: active === s.k ? HOUSE.accent : HOUSE.dim, borderBottom: `2px solid ${active === s.k ? HOUSE.accent : 'transparent'}`, marginBottom: -1 }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      {crumbs.length > 0 && (
        <nav className="flex items-center gap-1 flex-wrap" data-testid="platform-admin-crumbs" style={{ padding: '10px 16px 0 16px', fontSize: 12, color: HOUSE.dim }}>
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3" />}
              {c.to ? <button onClick={() => navigate(c.to)} style={{ background: 'none', border: 'none', color: HOUSE.mute, cursor: 'pointer', padding: 0, fontSize: 12 }}>{c.label}</button>
                    : <span style={{ color: HOUSE.body }}>{c.label}</span>}
            </span>
          ))}
        </nav>
      )}
      {children}
    </div>
  );
}
